import { useEffect, useMemo, useRef } from 'react';
import { useFormikContext } from 'formik';

import { RegistryId } from '@/react/portainer/registries/types/registry';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { FormControl } from '@@/form-components/FormControl';
import { Option, PortainerSelect } from '@@/form-components/PortainerSelect';
import { AutocompleteSelect } from '@@/form-components/AutocompleteSelect';
import { TextTip } from '@@/Tip/TextTip';

import { useAddonImageTags } from '../queries/useAddonImageTags';
import { useAddonRegistries } from '../queries/useAddonRegistries';
import { useAddonRegistryRepositories } from '../queries/useAddonRegistryRepositories';
import { useAddonRepositoryKind } from '../queries/useAddonRepositoryKind';
import { useAddonEnvironmentId } from '../useAddonEnvironmentId';

import { InstallFormValues } from './types';
import { matchingRepositories } from './repositorySuggestions';
import { RepositoryKindMismatch } from './RepositoryKindMismatch';
import { registryLabel } from './registryLabel';

/** Registry id standing for "use the repository the chart ships with". */
const CHART_DEFAULT_REGISTRY_ID = 0;

/**
 * AddonImageFormSection picks the add-on's container image the same way the chart
 * above it is picked: a registry, then a path within it, searched from the
 * registry's own catalog. An air-gapped install needs both the chart and the image
 * to come from a reachable registry.
 */
export function AddonImageFormSection({ readOnly }: { readOnly?: boolean }) {
  const { values, setFieldValue } = useFormikContext<InstallFormValues>();
  const environmentId = useAddonEnvironmentId().data;
  const registriesQuery = useAddonRegistries(environmentId);
  const registries = registriesQuery.data ?? [];

  const { imageRegistryId, imagePath } = values;
  const tagsQuery = useAddonImageTags({
    registryId: imageRegistryId,
    repository: imagePath,
    environmentId,
  });
  const tags = tagsQuery.data ?? [];
  const newestTag = tags[0];

  // Choosing an image selects its newest tag, the same way choosing a chart selects
  // its latest version. Armed with the path just picked, so it fires once that
  // path's tags arrive and not again. Only picking an image here arms it: an
  // installed add-on left on the chart's own tag must stay that way, or seeding
  // would pin a tag nobody chose and offer to save it. Skipped once a tag is
  // already set, so it never overwrites a tag typed in by hand while the image
  // field is still being edited.
  const armedPath = useRef<string | null>(null);
  useEffect(() => {
    if (
      armedPath.current !== imagePath ||
      !newestTag ||
      values.values.imageTag
    ) {
      return;
    }
    armedPath.current = null;
    setFieldValue('values.imageTag', newestTag);
  }, [imagePath, newestTag, values.values.imageTag, setFieldValue]);

  return (
    <>
      <FormControl
        label="Image registry"
        tooltip="Registry holding the add-on's container image. Its credentials are also what the add-on's pods pull with. Leave on the chart default to use the repository the chart ships with."
        size="vertical"
        inputId="addon-image-registry"
        errors={registriesQuery.isError && 'Unable to load registries.'}
      >
        <PortainerSelect<RegistryId>
          inputId="addon-image-registry"
          data-cy="addon-install-image-registry"
          value={imageRegistryId ?? CHART_DEFAULT_REGISTRY_ID}
          options={[
            { label: 'Chart default', value: CHART_DEFAULT_REGISTRY_ID },
            ...registries.map((registry) => ({
              label: registryLabel(registry.URL, registry.Name),
              value: registry.Id,
            })),
          ]}
          isLoading={registriesQuery.isInitialLoading}
          onChange={handleRegistryChange}
          disabled={readOnly}
        />
      </FormControl>

      <ImageField
        registryId={imageRegistryId}
        environmentId={environmentId}
        value={imagePath}
        readOnly={readOnly}
        onChange={handleImageChange}
      />

      <ImageTagField
        registryId={imageRegistryId}
        repository={imagePath}
        tags={tags}
        readOnly={readOnly}
      />
    </>
  );

  // Changing the registry invalidates the path, and the path invalidates the tag:
  // each is listed from the one above it.
  function handleRegistryChange(registryId: RegistryId | null) {
    setFieldValue('imageRegistryId', registryId || undefined);
    setFieldValue('imagePath', '');
    setFieldValue('values.imageTag', '');
  }

  // Trimmed as it is entered, like the chart path above: the tag list is looked
  // up from this value, so whitespace left on a pasted path would come back with
  // no tags and nothing to explain why. The tag itself is left untouched — it is
  // only ever set for the user by the auto-select above, never cleared by editing
  // the image, so a tag typed in by hand survives further edits to the image.
  function handleImageChange(path: string) {
    const trimmed = path.trim();
    setFieldValue('imagePath', trimmed);
    armedPath.current = trimmed || null;
  }
}

function ImageField({
  registryId,
  environmentId,
  value,
  readOnly,
  onChange,
}: {
  registryId?: RegistryId;
  environmentId?: EnvironmentId;
  value: string;
  readOnly?: boolean;
  onChange: (path: string) => void;
}) {
  const repositoriesQuery = useAddonRegistryRepositories(
    registryId,
    environmentId
  );
  const repositories = repositoriesQuery.data;
  const kindQuery = useAddonRepositoryKind({
    registryId,
    repository: value,
    environmentId,
  });

  // Suggestions only once something is typed: a registry can hold hundreds of
  // repositories, and an empty field means "use the chart's own repository".
  const suggestions = useMemo(
    () => matchingRepositories(value, repositories),
    [repositories, value]
  );

  return (
    <FormControl
      label="Image"
      size="vertical"
      inputId="addon-image-path"
      tooltip="Start typing to search the registry's images. Any path can be entered, so an image the registry does not list can still be used."
    >
      <AutocompleteSelect
        inputId="addon-image-path"
        data-cy="addon-install-image-path"
        value={value}
        onChange={onChange}
        searchResults={suggestions}
        placeholder={
          registryId ? 'e.g. portainer/portainer-run' : 'Chart default'
        }
        readOnly={readOnly}
        disabled={readOnly}
      />
      <RepositoryKindMismatch actual={kindQuery.data} expected="image" />
      {/* Listing is only there to suggest: Docker Hub and Quay serve no catalog
          at all yet are deliberately offered here, since an image needs no
          catalog to be pulled. Say so, or the field reads as a search box that
          silently never suggests anything. */}
      {repositoriesQuery.isError && (
        <TextTip color="blue" className="mt-2">
          This registry&apos;s images could not be listed, so no suggestions are
          available. Enter the image&apos;s path directly.
        </TextTip>
      )}
    </FormControl>
  );
}

// Suggests the tags published for the selected image, the same way the chart's
// version is picked, but any tag can also be typed directly — the image may be
// pulled from somewhere the lookup can't list. Left unset, the chart's own
// appVersion applies.
function ImageTagField({
  registryId,
  repository,
  tags,
  readOnly,
}: {
  registryId?: RegistryId;
  repository: string;
  tags: Array<string>;
  readOnly?: boolean;
}) {
  const { values, setFieldValue } = useFormikContext<InstallFormValues>();
  const current = values.values.imageTag;

  const suggestions = useMemo(
    () => matchingTags(current, tags),
    [tags, current]
  );

  return (
    <FormControl
      label="Image tag"
      size="vertical"
      inputId="addon-image-tag"
      tooltip="Leave empty to use the version the chart ships with. Any tag can be entered, so a tag not listed here can still be used."
    >
      <AutocompleteSelect
        inputId="addon-image-tag"
        data-cy="addon-install-image-tag"
        value={current}
        onChange={(tag) => setFieldValue('values.imageTag', tag.trim())}
        searchResults={suggestions}
        placeholder={tagPlaceholder()}
        readOnly={readOnly}
        disabled={readOnly}
      />
    </FormControl>
  );

  function tagPlaceholder() {
    if (tags.length || !registryId) {
      return 'Chart default';
    }
    if (!repository) {
      return 'Select an image to list tags';
    }

    return 'No tags found';
  }
}

// Every tag is offered while nothing has been typed, since a repository rarely
// holds enough tags to need narrowing first — unlike the image path field above,
// which only suggests once something is typed.
function matchingTags(
  value: string,
  tags: Array<string>
): Array<Option<string>> {
  return tags
    .filter((tag) => !value || tag.toLowerCase().includes(value.toLowerCase()))
    .map((tag) => ({ label: tag, value: tag }));
}
